import { useMemo } from 'react';
import * as R from 'remeda';
import { useIntl } from 'react-intl';
import { z } from 'zod';
import { HttpError } from '@rossum/api-client/errors';
import { Stack, Typography } from '@rossum/ui/material';
import { apiUrl } from '../../../../../constants/config';
import { ResultsPerStatus } from '../../../hooks/usePatchSchemas';
import { FlatSchemaWithQueues } from '../../../types/schema';
import { resolveQueuesDisplayedValue } from '../columns/filterOperators';
import { collectStringArrays } from './collectStringArrays';
import { ErrorKey } from './ErrorKey';

const plainObjectSchema = z.record(z.string(), z.unknown());

type Props = {
  rejectedRequests: ResultsPerStatus['rejected'];
  schemas: FlatSchemaWithQueues[];
};

export const ErrorDialogContent = ({ rejectedRequests, schemas }: Props) => {
  const intl = useIntl();
  const rejectedSchemaErrors = rejectedRequests.filter(
    (rejected): rejected is HttpError => {
      return (
        'data' in rejected &&
        'endpoint' in rejected &&
        'name' in rejected &&
        rejected.name === 'HttpError'
      );
    }
  );

  const schemasUrlMap = useMemo(
    () => R.fromEntries(schemas.map(schema => [schema.url, schema])),
    [schemas]
  );

  return (
    <Stack spacing={1}>
      <Typography variant="body2">
        {intl.formatMessage({
          id: 'features.fieldManager.fieldDetail.errors.errorDialogContent.description',
        })}
      </Typography>
      <Stack spacing={2}>
        {rejectedSchemaErrors.map(({ endpoint, data }) => {
          const schema = schemasUrlMap[`${apiUrl}${endpoint}`];
          if (!schema) {
            return null;
          }

          const parsed = plainObjectSchema.safeParse(data);
          const errors = parsed.success ? collectStringArrays(parsed.data) : [];
          const parsedSuccessfully = errors.length > 0;

          return (
            <Stack key={endpoint}>
              <Typography sx={{ fontWeight: 'bold' }} key={endpoint}>
                {resolveQueuesDisplayedValue(schema.queues, schema.id)}
              </Typography>
              {parsedSuccessfully ? (
                errors.map(error =>
                  Object.entries(error).map(([key, value]) => (
                    <div key={key}>
                      <ErrorKey pathKey={key} />{' '}
                      <Typography component="span" variant="body2">
                        {value.join(', ')}
                      </Typography>
                    </div>
                  ))
                )
              ) : (
                <Typography
                  variant="body2"
                  component="pre"
                  sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}
                >
                  {JSON.stringify(data, null, 2)}
                </Typography>
              )}
            </Stack>
          );
        })}
      </Stack>
    </Stack>
  );
};
